// R70 — WHAT A SPELL BECOMES WHEN IT LEAVES THE BOOK, AND THE LAW NOTHING COULD RUN.
//
// Every copy of a spell that leaves the Conjure Spellbook goes through `spellToReport`: the shelf
// row she saves, the PDF she downloads, and the working she sends to the Cauldron. It is a pure law
// and it sat inside App.tsx, so the only instrument that could reach it was a browser with a spell
// open — which is the reason roadmap 7 exists (R5's argument, unchanged).
//
// **WHAT IT DECIDES IS T9-2's LAW, AND THIS IS THE SHAPE THIS HOUSE HAS PAID FOR TWICE ALREADY.**
// Each block is guarded on its own list being non-empty, so *no body, no box* — a spell with no
// oils must not carry an "Oils" heading over nothing. W12-5 bought a live Cauldron compile and drove
// a browser to find the same shape printing "DATE OF BIRTH" over an empty line, and R63 found the
// seal's meaning heading drawn over `"".split()`'s one empty paragraph. Out here it is arithmetic on
// the returned sections.
//
// It imports NOTHING but types — the entry requirement that lets `scripts/r70-spell-browse.mjs`
// load and RUN it. `spellKicker` therefore takes the catalog reader as a PARAMETER, which is
// `CauldronList`'s shape and `saved-books.ts`'s: a law out here may not read `strings.js`, and a
// second English copy of three rows is R28/R29's disease.
import type { AnyReport } from "../components/ReportBody";

export type SpellStepCardLike = { [key: string]: unknown };

export type SpellLike = {
  id: string; slug: string; title: string; category: string; purpose: string;
  herbs: string[]; tools: string[]; oils: string[]; steps: string[]; source?: string;
  stepCards?: SpellStepCardLike[];
};

export type SpellSection = { heading: string; body: string; steps?: SpellStepCardLike[] };

// Where the spell came from, said plainly. A catalog recipe is HER OWN candle sheet, and it says so.
// The three rows are named here and resolved by the caller's reader, so this module holds the
// MAPPING (source -> which row) and never a second copy of the words.
export const SPELL_SOURCE_ROWS = {
  catalog: "spell.from.catalog",
  curated: "spell.from.curated",
  written: "spell.from.written",
} as const;

export function spellSourceRow(source?: string) {
  return source === "catalog" ? SPELL_SOURCE_ROWS.catalog
    : source === "curated" ? SPELL_SOURCE_ROWS.curated
    : SPELL_SOURCE_ROWS.written;
}

export function spellKicker(
  spell: SpellLike,
  language: string,
  read: (language: string, key: string) => string,
): string {
  return read(language, spellSourceRow(spell.source));
}

export function spellSections(spell: SpellLike): SpellSection[] {
  const sections: SpellSection[] = [{ heading: "Purpose", body: spell.purpose }];
  // T9-2: NO BODY, NO BOX. Each of these is a heading she reads, so an empty list draws nothing at
  // all rather than a heading over a blank.
  if (spell.herbs.length) sections.push({ heading: "Herbs", body: spell.herbs.map((h) => `• ${h}`).join("\n") });
  if (spell.tools.length) sections.push({ heading: "Tools", body: spell.tools.map((x) => `• ${x}`).join("\n") });
  if (spell.oils.length) sections.push({ heading: "Oils", body: spell.oils.map((o) => `• ${o}`).join("\n") });
  // T7-3: a catalog recipe's working carries its seal pictures into the saved copy and the Cauldron
  // send too, through the shared step-card shape the report renderer already draws.
  if (spell.steps.length) {
    sections.push({
      heading: "How to work it",
      body: spell.steps.map((st, i) => `${i + 1}. ${st}`).join("\n"),
      ...(spell.stepCards?.length ? { steps: spell.stepCards } : {}),
    });
  }
  return sections;
}

export function spellToReport(spell: SpellLike): AnyReport {
  return {
    type: "spell", kicker: "A Conjure Spell", spell,
    header: { name: "You", dateText: "", place: "" },
    overview: `${spell.title}. ${spell.purpose}`,
    sections: spellSections(spell),
  } as unknown as AnyReport;
}
